import { Head, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import FormTextarea from '@/Components/FormTextarea';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export default function Create({ material, flash }) {
    const reqNumber = material.formatted_number || String(material.number).padStart(4, '0');

    const { data, setData, post, processing, errors } = useForm({
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        sub_amount: '',
        tax: '',
        amount: '',
        invoice_file: null,
        notes: '',
    });

    const TAX_RATE = 0.05; // UAE VAT 5%

    // Auto-calculate tax (5%) and total when sub_amount changes
    const handleSubAmountChange = (value) => {
        const sub = parseFloat(value) || 0;
        const tax = (sub * TAX_RATE).toFixed(2);
        const total = (sub + parseFloat(tax)).toFixed(2);
        setData({ ...data, sub_amount: value, tax, amount: total });
    };

    // Allow manual tax override, recalculate total
    const handleTaxChange = (value) => {
        const sub = parseFloat(data.sub_amount) || 0;
        const tax = parseFloat(value) || 0;
        setData({ ...data, tax: value, amount: (sub + tax).toFixed(2) });
    };

    const submit = (e) => {
        e.preventDefault();
        post(`/materials/${material.id}/invoices`, {
            forceFormData: true,
        });
    };

    return (
        <AppLayout>
            <Head title="Add Invoice" />
            <PageHeader
                title="Add Invoice"
                subtitle={`For Requisition #${reqNumber} — ${material.supplier_name || ''}`}
                actions={
                    <Button href={`/materials/${material.id}/invoices`} variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </Button>
                }
            />

            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            <Card>
                <CardBody>
                    <form onSubmit={submit} className="space-y-6 max-w-lg">
                        <FormInput
                            label="Invoice Number"
                            value={data.invoice_number}
                            onChange={e => setData('invoice_number', e.target.value)}
                            error={errors.invoice_number}
                            placeholder="e.g. INV-001"
                            required
                        />

                        <FormInput
                            label="Invoice Date"
                            type="date"
                            value={data.invoice_date}
                            onChange={e => setData('invoice_date', e.target.value)}
                            error={errors.invoice_date}
                            required
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <FormInput
                                label="Sub Amount (AED)"
                                type="number"
                                step="0.01"
                                min="0"
                                value={data.sub_amount}
                                onChange={e => handleSubAmountChange(e.target.value)}
                                error={errors.sub_amount}
                                required
                            />
                            <FormInput
                                label="VAT 5% (AED)"
                                type="number"
                                step="0.01"
                                min="0"
                                value={data.tax}
                                onChange={e => handleTaxChange(e.target.value)}
                                error={errors.tax}
                                required
                            />
                            <FormInput
                                label="Total Amount (AED)"
                                type="number"
                                step="0.01"
                                min="0"
                                value={data.amount}
                                onChange={e => setData('amount', e.target.value)}
                                error={errors.amount}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice File</label>
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={e => setData('invoice_file', e.target.files[0])}
                                className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-900 hover:file:bg-emerald-100"
                            />
                            {errors.invoice_file && <p className="mt-1 text-xs text-red-500">{errors.invoice_file}</p>}
                            <p className="mt-1 text-xs text-slate-400">Accepts PDF, JPG, PNG. Max 10MB.</p>
                        </div>

                        <FormTextarea
                            label="Notes"
                            value={data.notes}
                            onChange={e => setData('notes', e.target.value)}
                            error={errors.notes}
                            rows={3}
                            placeholder="Optional notes about this invoice"
                        />

                        <div className="flex gap-3 pt-4">
                            <Button type="submit" disabled={processing}>
                                {processing ? 'Adding...' : 'Add Invoice'}
                            </Button>
                            <Button href={`/materials/${material.id}/invoices`} variant="outline">
                                Cancel
                            </Button>
                        </div>
                    </form>
                </CardBody>
            </Card>
        </AppLayout>
    );
}
